import { Crown, LogOut, MoreHorizontal, UserMinus, UserPlus, X } from '@/components/ui/icons';
import { useRef, useState } from 'react';
import { LIMITS, type ConversationDTO, type PublicUser } from '@clanfire/shared';
import { StaffBadge } from '@/components/profile/StaffBadge';
import { StyledName } from '@/components/profile/StyledName';
import { Avatar, statusLabel } from '@/components/ui/Avatar';
import { Button, IconButton } from '@/components/ui/Button';
import { Menu, type MenuEntry } from '@/components/ui/Popover';
import { leaveGroup, makeGroupOwner, removeFromGroup, useGroupMembers } from '@/lib/conversations';
import { cn } from '@/lib/utils';
import { useAuth } from '@/stores/auth';
import { useUi } from '@/stores/ui';
import { usePresence } from '@/stores/users';

/** The right-hand member list of a group DM, with the owner's tools. */
export function GroupMembers({ conversation, onClose }: { conversation: ConversationDTO; onClose?: () => void }) {
  const me = useAuth((s) => s.user);
  const members = useGroupMembers(conversation.id, me);
  const presences = usePresence((s) => s.statuses);
  const openModal = useUi((s) => s.openModal);
  const iOwn = conversation.ownerId === me?.id;
  const full = members.length >= LIMITS.groupDmMaxMembers;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex h-16 shrink-0 items-center justify-between px-5">
        <h2 className="text-lg text-fg">
          Members<span className="ml-1.5 font-sans text-sm text-fg-muted">{members.length}</span>
        </h2>
        {onClose && (
          <IconButton label="Close member list" size="sm" onClick={onClose}>
            <X size={18} />
          </IconButton>
        )}
      </div>
      <ul className="min-h-0 flex-1 overflow-y-auto px-3 pb-3">
        {members.map((user) => (
          <GroupMemberRow
            key={user.id}
            conversation={conversation}
            user={user}
            presence={presences[user.id] ?? 'offline'}
            isOwner={user.id === conversation.ownerId}
            isMe={user.id === me?.id}
            iOwn={iOwn}
          />
        ))}
      </ul>
      <div className="flex shrink-0 flex-col gap-2 border-t border-line p-3">
        <Button
          variant="secondary"
          size="sm"
          icon={<UserPlus size={16} />}
          disabled={full}
          title={full ? `Groups can have up to ${LIMITS.groupDmMaxMembers} people.` : undefined}
          onClick={() => openModal({ type: 'newDm', conversationId: conversation.id })}
        >
          Add friends
        </Button>
        <Button variant="ghost" size="sm" icon={<LogOut size={16} />} className="text-danger! hover:bg-danger-soft!" onClick={() => void leaveGroup(conversation)}>
          Leave group
        </Button>
      </div>
    </div>
  );
}

function GroupMemberRow({
  conversation,
  user,
  presence,
  isOwner,
  isMe,
  iOwn,
}: {
  conversation: ConversationDTO;
  user: PublicUser;
  presence: keyof typeof statusLabel;
  isOwner: boolean;
  isMe: boolean;
  iOwn: boolean;
}) {
  const openModal = useUi((s) => s.openModal);
  const [menuOpen, setMenuOpen] = useState(false);
  const moreRef = useRef<HTMLButtonElement>(null);
  const offline = presence === 'offline';
  const items: MenuEntry[] = [
    { label: 'Make group owner', icon: <Crown size={16} />, onSelect: () => void makeGroupOwner(conversation, user) },
    { label: 'Remove from group', icon: <UserMinus size={16} />, danger: true, onSelect: () => void removeFromGroup(conversation, user) },
  ];
  return (
    <li className="group/member relative">
      <button
        type="button"
        onClick={() => openModal({ type: 'profile', userId: user.id })}
        className={cn('flex w-full items-center gap-2.5 rounded-full p-1.5 pr-9 text-left transition-colors hover:bg-hover', offline && 'opacity-55 hover:opacity-100')}
      >
        <Avatar user={user} size={34} status={presence} ring="ring-panel" />
        <span className="min-w-0 flex-1 leading-tight">
          <span className="flex items-center gap-1">
            <StyledName user={user} className="truncate text-[13px] font-semibold">
              {user.displayName}
            </StyledName>
            {isMe && <span className="shrink-0 text-[11px] text-fg-muted">(you)</span>}
            {isOwner && <Crown size={13} className="shrink-0 text-warning" aria-label="Group owner" />}
            <StaffBadge role={user.staffRole} />
          </span>
          <span className="block truncate text-[11px] text-fg-muted">
            {user.customStatus && !offline ? `${user.customStatusEmoji ?? ''} ${user.customStatus}`.trim() : statusLabel[presence]}
          </span>
        </span>
      </button>
      {iOwn && !isMe && (
        <>
          <button
            ref={moreRef}
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={`Options for ${user.displayName}`}
            className={cn(
              'absolute top-1/2 right-1.5 -translate-y-1/2 rounded-full p-1.5 text-fg-muted transition-opacity hover:bg-active hover:text-fg focus:opacity-100',
              menuOpen ? 'opacity-100' : 'opacity-0 group-hover/member:opacity-100',
            )}
          >
            <MoreHorizontal size={16} />
          </button>
          <Menu open={menuOpen} onClose={() => setMenuOpen(false)} anchor={moreRef} placement="bottom-end" items={items} label={`Options for ${user.displayName}`} />
        </>
      )}
    </li>
  );
}
